import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from '../ui';
import { TransitionList } from './TransitionList';
import { ProjectRunsSection } from './ProjectRunsSection';
import { DecisionMirror } from './DecisionMirror';
import { ProjectEventLink } from './ProjectEventLink';
import { ProjectBriefBlock } from './ProjectBriefBlock';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../lib/api';
import {
  GTMProject,
  GTMProjectTransition,
  PIPELINE_STAGES,
  PipelineStage,
  STAGE_LABELS,
  StageSkill,
} from '../../types/pipeline';

interface BoardCardDetailProps {
  project: GTMProject;
  onClose: () => void;
  /** Refetch the board after any mutation so columns reflect the change. */
  onChanged: () => void;
}

/** What the ICP box starts with: a saved ICP, else the confirmed brief, as the server defaults. */
const icpBoxDefault = (project: GTMProject) => project.icp_text || project.brief_icp || '';

export const BoardCardDetail: React.FC<BoardCardDetailProps> = ({ project, onClose, onChanged }) => {
  const { showToast } = useToast();
  const [transitions, setTransitions] = useState<GTMProjectTransition[]>([]);
  const [stage, setStage] = useState<PipelineStage>(project.pipeline_stage);
  const [needsHuman, setNeedsHuman] = useState(project.needs_human);
  const [needsHumanReason, setNeedsHumanReason] = useState(project.needs_human_reason ?? null);
  const [notesMd, setNotesMd] = useState(project.notes_md);
  const notesRef = useRef<HTMLPreElement>(null);
  const [eventId, setEventId] = useState<string | null>(project.event_id ?? null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // SKY-541: reasons at write time. Transition reason rides the select inline;
  // the flag reason gets a small confirm row so flagging stays two clicks max.
  const [transitionReason, setTransitionReason] = useState('');
  const [flagPromptOpen, setFlagPromptOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  // SKY-552 run controls: the plays offered follow stage_skills, so a curation
  // change lands here without a deploy.
  const [stageSkills, setStageSkills] = useState<StageSkill[]>([]);
  const [skill, setSkill] = useState('');
  const [runTarget, setRunTarget] = useState('');
  const [runsKey, setRunsKey] = useState(0);
  const [focusListId, setFocusListId] = useState<string | null>(null);

  const loadTransitions = useCallback(async () => {
    try {
      setTransitions(await apiClient.getProjectTransitions(project.id, { limit: 10 }));
    } catch {
      // history is non-blocking
    }
  }, [project.id]);

  useEffect(() => {
    void loadTransitions();
  }, [loadTransitions]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getProjectStageSkills(project.id)
      .then((res) => {
        if (cancelled) return;
        setStageSkills(res.skills);
        setSkill(res.skills[0]?.name ?? '');
      })
      .catch(() => {
        // Non-blocking: no skills offered simply hides the run row.
        if (!cancelled) setStageSkills([]);
      });
    return () => {
      cancelled = true;
    };
    // Re-reads on stage change — the plays are stage-bound.
  }, [project.id, stage]);

  // Event-scoped planners run on the project's linked conference; icp-plan also
  // takes the text box as the ICP criteria rather than a sourcing target.
  const isAttendeePlay = skill === 'attendee-plan';
  const isIcpPlay = skill === 'icp-plan';

  useEffect(() => {
    const stored = icpBoxDefault(project);
    if (isIcpPlay && !runTarget && stored) setRunTarget(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIcpPlay]);

  const runBlocked = !skill || ((isAttendeePlay || isIcpPlay) && !eventId);

  const handleRun = async () => {
    if (runBlocked) return;
    setBusy(true);
    try {
      await apiClient.createRun(skill, {
        gtmProjectId: project.id,
        params: isAttendeePlay
          ? { event_id: eventId }
          : isIcpPlay
            ? { event_id: eventId, ...(runTarget.trim() ? { icp: runTarget.trim() } : {}) }
            : runTarget.trim()
              ? { target: runTarget.trim() }
              : undefined,
      });
      if (isIcpPlay && runTarget.trim() && runTarget.trim() !== icpBoxDefault(project)) {
        // Best-effort: the next run prefills instantly with what was actually used.
        apiClient.setPipelineIcpText(project.id, runTarget.trim()).catch(() => undefined);
      }
      if (!isIcpPlay) setRunTarget('');
      showToast(`${skill} queued — watch it in Agent runs`, 'success');
      // Bump the runs panel so the queued row (and its polling) start now.
      setRunsKey(k => k + 1);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not start the run', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleTransition = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const toStage = e.target.value as PipelineStage;
    if (toStage === stage) return;
    setBusy(true);
    try {
      const updated = await apiClient.transitionProject(
        project.id,
        toStage,
        transitionReason.trim() || undefined
      );
      setStage(updated.pipeline_stage);
      setTransitionReason('');
      showToast(`Moved to ${STAGE_LABELS[toStage]}`, 'success');
      onChanged();
      await loadTransitions();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Transition failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const applyNeedsHuman = async (flag: boolean, reason?: string) => {
    setBusy(true);
    try {
      const updated = await apiClient.setPipelineNeedsHuman(project.id, flag, reason);
      setNeedsHuman(updated.needs_human);
      setNeedsHumanReason(updated.needs_human_reason ?? null);
      setFlagPromptOpen(false);
      setFlagReason('');
      onChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Update failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleNeedsHuman = () => {
    if (needsHuman) {
      // Clearing needs no ceremony — the server wipes the stored reason.
      void applyNeedsHuman(false);
    } else {
      setFlagPromptOpen(open => !open);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const updated = await apiClient.appendPipelineNote(project.id, note.trim());
      setNotesMd(updated.notes_md);
      setNote('');
      showToast('Note added', 'success');
      onChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to add note', 'error');
    } finally {
      setBusy(false);
    }
  };

  const scrollNotesToLatest = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (e.currentTarget.open && notesRef.current) notesRef.current.scrollTop = notesRef.current.scrollHeight;
  };

  return (
    <Modal isOpen onClose={onClose} title={project.name} size="lg">
      <div className="space-y-4">
        {project.client_entity_name && (
          <p className="text-sm text-neutral-500">{project.client_entity_name}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <label htmlFor="stage-select" className="text-13 font-medium text-neutral-700">
            Stage
          </label>
          <select
            id="stage-select"
            value={stage}
            onChange={handleTransition}
            disabled={busy}
            className="text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50"
          >
            {PIPELINE_STAGES.map(s => (
              <option key={s} value={s}>
                {STAGE_LABELS[s]}
              </option>
            ))}
          </select>
          <input
            id="transition-reason"
            type="text"
            value={transitionReason}
            onChange={e => setTransitionReason(e.target.value)}
            placeholder="Why? (optional, sent with next stage change)"
            aria-label="Reason for the next stage change"
            maxLength={1000}
            disabled={busy}
            className="flex-1 min-w-40 text-xs border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50"
          />
          <Button
            variant={needsHuman ? 'danger' : 'secondary'}
            size="sm"
            onClick={handleToggleNeedsHuman}
            disabled={busy}
          >
            {needsHuman ? 'Clear needs-you' : 'Flag needs-you'}
          </Button>
        </div>

        {needsHuman && needsHumanReason && (
          <p className="text-xs text-warning-800 bg-warning-500/10 rounded px-2 py-1.5">
            Needs you: {needsHumanReason}
          </p>
        )}

        {flagPromptOpen && !needsHuman && (
          <div className="flex items-center gap-2">
            <input
              id="flag-reason"
              type="text"
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              onKeyDown={e => {
                // isComposing: Enter confirms an IME candidate (JP/CN input) —
                // submitting there would flag mid-word.
                if (e.key === 'Enter' && !busy && !e.nativeEvent.isComposing) {
                  void applyNeedsHuman(true, flagReason.trim() || undefined);
                }
              }}
              placeholder="Why does this need a human? (optional)"
              aria-label="Reason for flagging needs-you"
              maxLength={500}
              disabled={busy}
              autoFocus
              className="flex-1 text-xs border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => void applyNeedsHuman(true, flagReason.trim() || undefined)}
              disabled={busy}
            >
              Flag
            </Button>
          </div>
        )}

        <ProjectEventLink
          projectId={project.id}
          eventId={eventId}
          disabled={busy}
          onLinked={(id) => {
            setEventId(id);
            onChanged();
          }}
        />

        <ProjectBriefBlock projectId={project.id} />

        {notesMd && (
          <details onToggle={scrollNotesToLatest}>
            <summary className="text-13 font-semibold text-neutral-700 mb-1 cursor-pointer">Notes</summary>
            <pre ref={notesRef} className="text-xs text-neutral-600 whitespace-pre-wrap bg-neutral-50 rounded p-2 max-h-80 overflow-auto">
              {notesMd}
            </pre>
          </details>
        )}

        <div>
          <h4 className="text-13 font-semibold text-neutral-700 mb-1">Recent transitions</h4>
          <TransitionList transitions={transitions} />
        </div>

        <DecisionMirror projectId={project.id} refreshKey={runsKey} focusListId={focusListId} />

        {stageSkills.length > 0 && (
          <div>
            <h4 className="text-13 font-semibold text-neutral-700 mb-1">
              Run a play for {STAGE_LABELS[stage]}
            </h4>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                id="run-skill"
                value={skill}
                onChange={e => setSkill(e.target.value)}
                disabled={busy}
                aria-label="Skill to run"
                className="text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50"
              >
                {stageSkills.map(s => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
              {!isAttendeePlay && (
                <input
                  id="run-target"
                  type="text"
                  value={runTarget}
                  onChange={e => setRunTarget(e.target.value)}
                  placeholder={
                    isIcpPlay
                      ? "The client's ICP, e.g. clinical ops leaders (Director+) at pharma sponsors"
                      : "What to source (optional), e.g. ops leaders at Bay Area robotics firms"
                  }
                  aria-label="Target or ICP note passed to the run"
                  maxLength={2000}
                  disabled={busy}
                  className="flex-1 min-w-56 text-xs border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50"
                />
              )}
              <Button variant="primary" size="sm" onClick={handleRun} disabled={busy || runBlocked}>
                Run
              </Button>
            </div>
            {isAttendeePlay && !eventId && (
              <p className="text-xs text-warning-800 bg-warning-500/10 rounded px-2 py-1.5 mt-1">
                Link a conference above first. This play runs on the project's linked conference.
              </p>
            )}
            {stageSkills.find(s => s.name === skill)?.description && (
              <p className="text-xs text-neutral-500 mt-1">
                {stageSkills.find(s => s.name === skill)?.description}
              </p>
            )}
          </div>
        )}

        <ProjectRunsSection
          projectId={project.id}
          refreshKey={runsKey}
          onOpenList={setFocusListId}
        />

        <div>
          <label htmlFor="note-input" className="text-13 font-semibold text-neutral-700">
            Add note
          </label>
          <textarea
            id="note-input"
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            className="mt-1 w-full text-sm border border-neutral-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
            placeholder="Add a note to this project…"
          />
          <div className="flex justify-end mt-2">
            <Button variant="primary" size="sm" onClick={handleAddNote} disabled={busy || !note.trim()}>
              Add note
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
